import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api, setAuthToken, setUser } from '@/utils/api';
import { ShieldCheck, ArrowLeft, ExternalLink } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isExpiredDemo = searchParams.get('expired') === 'demo';
  const formRef = useRef(null);

  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/auth/login', loginData);
      const userRole = response.data.user?.role;
      if (userRole !== 'admin' && userRole !== 'demo') {
        toast.error('Access denied. Admin credentials required.');
        setLoading(false);
        return;
      }
      setAuthToken(response.data.token);
      setUser(response.data.user);
      toast.success(userRole === 'demo' ? 'Welcome to the Desko Demo!' : 'Admin login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Invalid email or password';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleTryDemo = () => {
    setLoginData({ email: 'demo@deskoapp.com', password: 'Test@Deskoapp' });
    // auto-submit after state flushes
    setTimeout(() => { if (formRef.current) formRef.current.requestSubmit(); }, 50);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-6 h-11" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Button>

        {/* Expired demo session message */}
        {isExpiredDemo && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800" data-testid="expired-demo-msg">
            Your demo session has ended. Thanks for exploring Desko!
            Log in as admin or try the demo again.
          </div>
        )}

        <Card className="border-border shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto h-16 w-16 rounded-full bg-accent/10 flex items-center justify-center mb-4">
              <ShieldCheck className="h-8 w-8 text-accent" />
            </div>
            <CardTitle className="text-2xl font-heading">Admin Portal</CardTitle>
            <CardDescription>Enter your admin credentials to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <form ref={formRef} onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="admin-email">Email</Label>
                <Input
                  id="admin-email"
                  type="email"
                  data-testid="admin-email-input"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  required
                  className="mt-1.5 text-base h-11"
                />
              </div>
              <div>
                <Label htmlFor="admin-password">Password</Label>
                <Input
                  id="admin-password"
                  type="password"
                  data-testid="admin-password-input"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  required
                  className="mt-1.5 text-base h-11"
                />
              </div>
              <Button type="submit" data-testid="admin-login-submit-btn" disabled={loading} className="w-full h-11">
                {loading ? 'Logging in...' : 'Login as Admin'}
              </Button>
            </form>

            {/* Divider */}
            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">or</span>
              <div className="flex-1 h-px bg-border" />
            </div>

            {/* Try Demo button */}
            <Button
              variant="outline"
              onClick={handleTryDemo}
              className="w-full h-11"
              data-testid="try-demo-btn"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Try Demo ↗
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Read-only. No real data will be changed.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogin;
